/**
 * CanvasOverlayManager — singleton overlay slot for canvas-area overlays (DR-8).
 *
 * Wraps the canvas content area. Listens to ShellEventBus for:
 *   - INVOKE_SOLUTION_RUN  → opens InvokeSolutionOverlay + bootstraps ESA conversation
 *   - SOLUTION_RUN_INITIATED → closes overlay + cleans up store and thread
 *   - SOLUTION_RUN_CANCELLED → same cleanup (no navigation)
 *
 * Overlay renders OPTIMISTICALLY (AR2-003): the form appears immediately on
 * INVOKE_SOLUTION_RUN; conversation bootstrap happens async in the background.
 * SolutionRunCard shows a loading state until conversationId is set.
 *
 * Cross-feature coupling note (DR-11): this component NEVER imports from
 * features/Esa/components/. All coordination is via ShellEventBus events.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { shellEventBus } from '../../../shell/events/eventBus';
import { getSolutionRunStore } from '../store/solutionRunStore';
import { useEsaDomainStore } from '../../Esa/store/esaDomainStore';
import { useShellStore } from '../../../shell/store/shellStore';
import { bootstrapConversation } from './bootstrapConversation';
import { InvokeSolutionOverlay } from './InvokeSolutionOverlay';
import { ESAMessageExpandOverlay } from './ESAMessageExpandOverlay';
import type { InvokeSolutionPayload } from '../types/solutionRun';

type ActiveOverlay =
  | { type: 'invoke-solution'; payload: InvokeSolutionPayload }
  | null;

interface CanvasOverlayManagerProps {
  children: React.ReactNode;
}

export function CanvasOverlayManager({ children }: CanvasOverlayManagerProps): React.ReactElement {
  const [activeOverlay, setActiveOverlay] = useState<ActiveOverlay>(null);
  // Ref mirrors state so async bootstrap callbacks and stable event handlers
  // always read the latest value without becoming stale-closure dependencies.
  const activeOverlayRef = useRef<ActiveOverlay>(null);
  const pendingConvIdRef = useRef<string | null>(null);

  // Message-expand overlay — driven by esaDomainStore (DR-8 singleton).
  const expandedMessage = useEsaDomainStore(s => s.expandedMessage);
  const clearExpandedMessage = useEsaDomainStore(s => s.clearExpandedMessage);

  // Keep ref in sync with state
  useEffect(() => {
    activeOverlayRef.current = activeOverlay;
  }, [activeOverlay]);

  // Initiated close — run card removed, overlay closed, but conversation thread
  // stays open so the user can continue chatting on the same thread.
  const handleInitiated = useCallback(() => {
    getSolutionRunStore().getState().reset();
    pendingConvIdRef.current = null;
    setActiveOverlay(null);
  }, []);

  // Full teardown — called on CANCELLED only. Removes the thread entirely.
  const handleClose = useCallback(() => {
    const store = getSolutionRunStore().getState();
    const convId = store.conversationId ?? pendingConvIdRef.current;
    store.reset();
    pendingConvIdRef.current = null;
    if (convId) {
      useEsaDomainStore.getState().removeThread(convId);
    }
    setActiveOverlay(null);
  }, []);

  // Modal-only close — called on SOLUTION_RUN_MODAL_CLOSE ("Review & initiate").
  // Closes the overlay UI but keeps the store alive (status moves to 'reviewing').
  const handleModalClose = useCallback(() => {
    setActiveOverlay(null);
  }, []);

  // Reopen — called on SOLUTION_RUN_REOPEN_MODAL ("Keep Editing").
  // Sets status back to 'drafting' with all current form values intact;
  // no new conversation bootstrap needed.
  const handleReopen = useCallback(() => {
    const store = getSolutionRunStore().getState();
    const payload = store.originalPayload;
    if (!payload) return;
    store.setDrafting();
    setActiveOverlay({ type: 'invoke-solution', payload });
  }, []);

  // Invoke handler — reads overlay state via ref (no stale closure loop)
  const handleInvoke = useCallback(async (payload: InvokeSolutionPayload) => {
    const store = getSolutionRunStore();

    // Singleton: if overlay is already open, close previous (DR-8)
    if (activeOverlayRef.current !== null) {
      const prevConvId = store.getState().conversationId ?? pendingConvIdRef.current;
      store.getState().reset();
      pendingConvIdRef.current = null;
      if (prevConvId) {
        useEsaDomainStore.getState().removeThread(prevConvId);
      }
    }

    // Initialize store + open overlay IMMEDIATELY — optimistic (AR2-003)
    store.getState().initialize(payload);
    setActiveOverlay({ type: 'invoke-solution', payload });

    // Bootstrap ESA conversation async in background
    try {
      const realId = await bootstrapConversation(payload);
      pendingConvIdRef.current = realId;
      store.getState().setConversationId(realId);
      // Open ESA panel in chat mode so the SolutionRunCard appears in the conversation thread
      useShellStore.getState().setEsaPanelOpen(true);
      useShellStore.getState().setEsaPanelMode('chat');
    } catch {
      // Degraded mode: form still usable, card shows "ESA sync unavailable" (AR2-002)
      store.getState().setDegraded();
      shellEventBus.dispatch({
        type: 'TOAST_NOTIFICATION',
        payload: {
          message: 'Could not connect to ESA. Fill in the form and Review & Initiate manually.',
          severity: 'warn',
        },
      });
    }
  }, []); // stable — reads overlay state via activeOverlayRef

  // Wire event subscriptions on mount — stable handlers, no re-subscription loop
  useEffect(() => {
    const unsub1 = shellEventBus.on('INVOKE_SOLUTION_RUN', event => {
      void handleInvoke(event.payload);
    });
    const unsub2 = shellEventBus.on('SOLUTION_RUN_INITIATED', handleInitiated);
    const unsub3 = shellEventBus.on('SOLUTION_RUN_CANCELLED', handleClose);
    const unsub4 = shellEventBus.on('SOLUTION_RUN_MODAL_CLOSE', handleModalClose);
    const unsub5 = shellEventBus.on('SOLUTION_RUN_REOPEN_MODAL', handleReopen);

    return () => {
      unsub1();
      unsub2();
      unsub3();
      unsub4();
      unsub5();
    };
  }, [handleInvoke, handleInitiated, handleClose, handleModalClose, handleReopen]);

  // Escape key — only active while an overlay is open
  useEffect(() => {
    if (!activeOverlay) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        shellEventBus.dispatch({ type: 'SOLUTION_RUN_CANCELLED' });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeOverlay]);

  const handleDiscard = useCallback(() => {
    shellEventBus.dispatch({ type: 'SOLUTION_RUN_CANCELLED' });
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {children}
      {activeOverlay?.type === 'invoke-solution' && (
        <InvokeSolutionOverlay
          payload={activeOverlay.payload}
          onDiscard={handleDiscard}
        />
      )}
      {expandedMessage !== null && (
        <ESAMessageExpandOverlay
          message={expandedMessage}
          onClose={clearExpandedMessage}
        />
      )}
    </div>
  );
}
